import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { handleAgentExecutionEvent } from "@/queries/agent-queries";
import { handleAutomationEvents } from "@/queries/automations-queries";
import { handleEnvironmentClassEvent, handleEnvironmentEvent } from "@/queries/environment-queries";
import { handleGroupMembershipEvent, handleRoleAssignmentEvent } from "@/queries/group-queries";
import { handleOrganizationPolicyEvent } from "@/queries/organization-queries";
import { handlePrebuildEvent } from "@/queries/prebuild-queries";
import { handleProjectEvent } from "@/queries/project-queries";
import { handlePromptEvent } from "@/queries/prompt-queries";
import { handleRunnerEvent } from "@/queries/runner-queries";
import { handleProjectSecretEvent } from "@/queries/secret-queries";
import { handleUserSecretEvent } from "@/queries/secret-queries";
import { handleServiceAccountEvent } from "@/queries/service-account-queries";
import { handleUserPreferenceEvent } from "@/queries/user-preferences-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { handleWorkflowEvents } from "@/queries/automation-queries";
import { trackStreamEnd, trackStreamStart } from "@/utils/memory-diagnostics";
import { Code, ConnectError } from "@connectrpc/connect";
import { useQueryClient } from "@tanstack/react-query";
import { ResourceOperation, ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { useEffect, useRef } from "react";

type Scope =
    | {
          case: "environmentId";
          value: string;
      }
    | {
          case: "organization";
          value: true;
      };

export const useStreamEvents = (scope: Scope = { case: "organization", value: true }) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const client = useQueryClient();

    const abortControllerRef = useRef(new AbortController());
    useEffect(() => {
        if (!client) {
            console.log("no client, aborting stream events for", scope.case);
            return;
        }

        if (!user) {
            console.log("no user, aborting stream events for", scope.case);
            return;
        }
        if (scope.case === "environmentId" && !scope.value) {
            return;
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const scopeLabel = scope.case === "environmentId" ? `environment ${scope.value}` : "organization";
        const streamId = `watchEvents:${scopeLabel}:${Date.now()}`;
        
        trackStreamStart(streamId);
        
        runUntilAborted(abortController, async () => {
            console.debug(`[WatchEvents] Starting stream for ${scopeLabel}`);
            
            try {
                for await (const response of api.eventService.watchEvents(
                    {
                        scope,
                    },
                    {
                        signal: abortController.signal,
                        timeoutMs: -1, // disable default timeout
                    },
                )) {
                    if (abortController.signal.aborted) {
                        console.debug(`[WatchEvents] Stream aborted for ${scopeLabel}`);
                        return;
                    }

                    console.debug(`[WatchEvents] Received event for ${scopeLabel}:`, {
                        resourceType: nameOfResourceType(response.resourceType),
                        operation: nameOfOperation(response.operation),
                        resourceId: response.resourceId,
                    });

                    switch (response.resourceType) {
                    case ResourceType.ENVIRONMENT:
                        await handleEnvironmentEvent(api, client, response);
                        break;
                    case ResourceType.RUNNER:
                        await handleRunnerEvent(api, client, response);
                        break;
                    case ResourceType.ENVIRONMENT_CLASS:
                        await handleEnvironmentClassEvent(client);
                        break;
                    case ResourceType.PROJECT:
                        await handleProjectEvent(api, client, response);
                        break;
                    case ResourceType.PREBUILD:
                        await handlePrebuildEvent(api, client, response);
                        break;
                    case ResourceType.TASK:
                    case ResourceType.SERVICE:
                    case ResourceType.TASK_EXECUTION: {
                        const environmentId = scope.case === "environmentId" ? scope.value : undefined;
                        if (!environmentId) {
                            console.error("Received automation event outside of environment scope", response);
                            break;
                        }
                        await handleAutomationEvents(api, client, response, environmentId);
                        break;
                    }
                    case ResourceType.USER_PREFERENCE: {
                        await handleUserPreferenceEvent(api, client, response);
                        break;
                    }
                    case ResourceType.SECRET: {
                        await handleProjectSecretEvent(api, client, response);
                        break;
                    }
                    case ResourceType.USER_SECRET: {
                        await handleUserSecretEvent(api, client, response);
                        break;
                    }
                    case ResourceType.AGENT_EXECUTION: {
                        await handleAgentExecutionEvent(api, client, response);
                        break;
                    }
                    case ResourceType.ORGANIZATION_POLICY: {
                        const organizationId = scope.case === "organization" ? user?.organizationId : undefined;
                        if (!organizationId) {
                            console.error("Received organization policy event outside of organization scope", response);
                            break;
                        }
                        await handleOrganizationPolicyEvent(api, client, response, organizationId);
                        break;
                    }
                    case ResourceType.PROMPT: {
                        handlePromptEvent(api, client, response);
                        break;
                    }
                    case ResourceType.WORKFLOW:
                    case ResourceType.WORKFLOW_EXECUTION:
                    case ResourceType.WORKFLOW_EXECUTION_ACTION: {
                        await handleWorkflowEvents(api, client, response);
                        break;
                    }
                    case ResourceType.ROLE_ASSIGNMENT_CHANGED: {
                        await handleRoleAssignmentEvent(client);
                        break;
                    }
                    case ResourceType.GROUP_MEMBERSHIP_CHANGED: {
                        await handleGroupMembershipEvent(client);
                        break;
                    }
                    case ResourceType.SERVICE_ACCOUNT: {
                        await handleServiceAccountEvent(api, client, response);
                        break;
                    }
                    default:
                        console.warn(`[WatchEvents] Unhandled resource type for ${scopeLabel}:`, response.resourceType);
                }
            }
            console.debug(`[WatchEvents] Stream ended normally for ${scopeLabel}`);
            } catch (error) {
                // Check if this is an intentional cancellation
                if (error instanceof ConnectError && error.code === Code.Canceled) {
                    console.debug(`[WatchEvents] Stream canceled for ${scopeLabel}`);
                    return;
                }
                
                // Check if abort was triggered (might happen before the error is thrown)
                if (abortController.signal.aborted) {
                    console.debug(`[WatchEvents] Stream aborted during operation for ${scopeLabel}`);
                    return;
                }
                
                console.error(`[WatchEvents] Stream error for ${scopeLabel}:`, error);
                throw error;
            }
        }).catch((error) => {
            // Double-check for cancellation in the outer catch
            if (error instanceof ConnectError && error.code === Code.Canceled) {
                console.debug(`[WatchEvents] Stream cleanup after cancellation for ${scopeLabel}`);
                return;
            }
            
            if (abortController.signal.aborted) {
                console.debug(`[WatchEvents] Stream cleanup after abort for ${scopeLabel}`);
                return;
            }
            console.error(`[WatchEvents] Unhandled error for ${scopeLabel}:`, error);
        });

        return () => {
            const scopeLabel = scope.case === "environmentId" ? `environment ${scope.value}` : "organization";
            console.debug(`[WatchEvents] Cleanup triggered for ${scopeLabel}`);
            trackStreamEnd(streamId);
            abortController.abort(`Cleanup - ${scopeLabel}`);
        };
    }, [api, client, scope, user]);

    return {
        unsubscribe: () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort("Manual unsubscribe");
            }
        },
    };
};

async function runUntilAborted(abortcontroller: AbortController, asyncFunction: () => Promise<void>) {
    while (!abortcontroller.signal.aborted) {
        try {
            await asyncFunction();
        } catch (error) {
            if (error instanceof ConnectError) {
                // These errors should stop retrying
                if (
                    error.code === Code.Canceled ||
                    error.code === Code.NotFound ||
                    error.code === Code.PermissionDenied ||
                    error.code === Code.Unauthenticated ||
                    error.code === Code.Internal
                ) {
                    abortcontroller.abort(`Stream error: ${error.code}`);
                    return;
                }
            }
            if (abortcontroller.signal.aborted) {
                return;
            }
            // let's wait a sec before retrying
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    }
}

function nameOfResourceType(resourceType: ResourceType): string {
    return ResourceType[resourceType] ?? "UNKNOWN_RESOURCE_TYPE";
}

function nameOfOperation(operation: number): string {
    return ResourceOperation[operation] ?? "UNKNOWN_OPERATION";
}
